import { HeaderComponent } from 'components/header/component'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import React, { Component } from 'react'
import { Button, Col, Row, Card, CardBody, CardHeader, Alert, Badge, Table, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import * as Redux from 'redux'
import { injectReducer } from 'store/index'
import * as GenericRedux from 'generic/genericRedux'
import * as Flash from 'components/flash'

import './_styles.scss'
import * as Constants from './constants'
import * as Types from './types'

class CloudWatchMonitoringComponent extends Component<Types.Props, Types.State> {
  public static contextType = RasaContext
  private streamBuffer: string = ''

  constructor(props: Types.Props) {
    super(props)
    this.state = {
      data: {
        lambdaErrors: [],
        ecsErrors: [],
        summary: {
          totalErrors: 0,
          criticalErrors: 0,
          highErrors: 0,
          mediumErrors: 0,
          lowErrors: 0,
          errorsByService: {
            lambda: 0,
            ecs: 0,
            sqs: 0
          },
        },
        lastUpdated: '',
      },
      error: '',
      isLoading: false,
      selectedDate: this.getTodayDate(),
      streaming: {
        isStreaming: false,
        progress: {
          current: 0,
          total: 0,
          service: '',
          message: ''
        },
        canCancel: false,
        streamedData: {
          lambdaErrors: [],
          ecsErrors: []
        }
      },
      pagination: {
        lambda: { currentPage: 1, itemsPerPage: 10 },
        ecs: { currentPage: 1, itemsPerPage: 10 },
      },
      logStreamModal: {
        isOpen: false,
        selectedError: null,
        logEvents: [],
        totalEvents: 0,
        aiAnalysis: '',
        analyzedAt: '',
        isLoadingLogs: false,
        error: '',
        logEventsPagination: {
          currentPage: 1,
          itemsPerPage: 10,
        },
      },
      jiraModal: {
        isOpen: false,
        selectedError: null,
        isCreatingTicket: false,
      },
    }
  }

  public componentDidMount = () => {
    this.context.user.init().then(() => {
      this.loadCloudWatchDataStream()
    })
  }

  public render() {
    return <div className="cloudwatch-monitoring-wrapper">
      <HeaderComponent
        title={'AWS CLOUDWATCH MONITORING'}
        subTitle={'Error Monitoring Dashboard'}
        description={[
          'Monitor AWS CloudWatch logs for Lambda, ECS, and SQS errors with AI-powered insights.',
        ]}/>
      
      <div className="date-filter-section">
        <Row>
          <Col md="6">
            <div className="date-filter">
              <label htmlFor="dateFilter">Filter by Date:</label>
              <input 
                id="dateFilter"
                type="date" 
                className="form-control"
                value={this.state.selectedDate}
                onChange={(e) => this.setState({ selectedDate: e.target.value }, () => {
                  this.loadCloudWatchDataStream()
                })}
              />
            </div>
          </Col>
          <Col md="6" className="text-right">
            <div className="d-flex justify-content-end gap-2">
              {this.state.streaming.isStreaming && this.state.streaming.canCancel ? (
                <Button 
                  color="secondary" 
                  size="sm"
                  onClick={this.cancelStreaming}
                >
                  Cancel
                </Button>
              ) : null}
              <Button 
                color="secondary" 
                size="sm"
                onClick={this.refreshData}
                disabled={this.state.isLoading || this.state.streaming.isStreaming}
              >
                {this.state.streaming.isStreaming ? 'Loading...' : 
                 this.state.isLoading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {this.state.error.length > 0 && (
        <Alert color="danger">
          {this.state.error}
        </Alert>
      )}

      {this.state.streaming.isStreaming && this.renderProgressIndicator()}

      {this.state.isLoading && !this.state.streaming.isStreaming ? (
        <Loading size="64"/>
      ) : (
        <div>
          {this.renderOverviewCards()}
          {this.renderAISummary()}
          {this.renderLambdaErrors()}
          {this.renderECSErrors()}
          {this.renderLogStreamModal()}
          {this.getJiraModalJSX()}
        </div>
      )}
    </div>
  }

  private renderOverviewCards = () => {
    const { data } = this.state
    return (
      <div className="overview-cards">
        <Row>
          <Col md="4">
            <Card className="overview-card">
              <CardBody>
                <div className="card-content">
                  <div className="card-icon total-errors">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <div className="card-info">
                    <h3>{data.summary.totalErrors}</h3>
                    <p>Total Errors</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="overview-card">
              <CardBody>
                <div className="card-content">
                  <div className="card-icon critical-errors">
                    <i className="fas fa-times-circle"></i>
                  </div>
                  <div className="card-info">
                    <h3>{data.summary.criticalErrors}</h3>
                    <p>Critical Errors</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="overview-card">
              <CardBody>
                <div className="card-content">
                  <div className="card-icon high-errors">
                    <i className="fas fa-exclamation"></i>
                  </div>
                  <div className="card-info">
                    <h3>{data.summary.highErrors}</h3>
                    <p>High Errors</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md="6">
            <Card className="service-card">
              <CardBody>
                <div className="card-content">
                  <div className="card-icon lambda-service">
                    <i className="fab fa-aws"></i>
                  </div>
                  <div className="card-info">
                    <h3>{data.summary.errorsByService.lambda}</h3>
                    <p>Lambda Errors</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="service-card">
              <CardBody>
                <div className="card-content">
                  <div className="card-icon ecs-service">
                    <i className="fas fa-server"></i>
                  </div>
                  <div className="card-info">
                    <h3>{data.summary.errorsByService.ecs}</h3>
                    <p>ECS Errors</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    )
  }

  private renderAISummary = () => {
    return null
  }

  private renderLambdaErrors = () => {
    const { data, pagination } = this.state
    const { currentPage, itemsPerPage } = pagination.lambda
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedErrors = data.lambdaErrors.slice(startIndex, endIndex)
    const totalPages = Math.ceil(data.lambdaErrors.length / itemsPerPage)
    
    return (
      <Card className="error-section">
        <CardHeader>
          <h5><i className="fab fa-aws"></i> Lambda Errors</h5>
          <Badge color="danger">{data.lambdaErrors.length}</Badge>
        </CardHeader>
        <CardBody>
          {data.lambdaErrors.length > 0 ? (
            <>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Function Name</th>
                    <th>Error Type</th>
                    <th>Message</th>
                    <th>First Occurrence</th>
                    <th>Last Occurrence</th>
                    <th>Count</th>
                    <th>Severity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedErrors.map((error, index) => (
                    <tr key={index}>
                      <td><code>{error.functionName}</code></td>
                      <td><Badge color="warning">{error.errorType}</Badge></td>
                      <td className="error-message">{error.message}</td>
                      <td>{error.firstOccurrence}</td>
                      <td>{error.lastOccurrence}</td>
                      <td>
                        <Badge color="info">{error.count}</Badge>
                      </td>
                      <td>
                        <Badge color={this.getSeverityColor(error.severity)}>
                          {error.severity}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={() => this.openLogStreamModal(error)}
                          >
                            View Details
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={() => this.createJiraTicket(error)}
                          >
                            Create JIRA
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {this.renderPagination('lambda', currentPage, totalPages)}
            </>
          ) : (
            <div className="no-errors">
              <i className="fas fa-check-circle text-success"></i>
              <p>No Lambda errors found for the selected date.</p>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderECSErrors = () => {
    const { data, pagination } = this.state
    const { currentPage, itemsPerPage } = pagination.ecs
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedErrors = data.ecsErrors.slice(startIndex, endIndex)
    const totalPages = Math.ceil(data.ecsErrors.length / itemsPerPage)
    
    return (
      <Card className="error-section">
        <CardHeader>
          <h5><i className="fas fa-server"></i> ECS Errors</h5>
          <Badge color="danger">{data.ecsErrors.length}</Badge>
        </CardHeader>
        <CardBody>
          {data.ecsErrors.length > 0 ? (
            <>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Service Name</th>
                    <th>Error Type</th>
                    <th>Message</th>
                    <th>First Occurrence</th>
                    <th>Last Occurrence</th>
                    <th>Count</th>
                    <th>Severity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedErrors.map((error, index) => (
                    <tr key={index}>
                      <td><code>{error.serviceName}</code></td>
                      <td><Badge color="warning">{error.errorType}</Badge></td>
                      <td className="error-message">{error.message}</td>
                      <td>{error.firstOccurrence}</td>
                      <td>{error.lastOccurrence}</td>
                      <td>
                        <Badge color="info">{error.count}</Badge>
                      </td>
                      <td>
                        <Badge color={this.getSeverityColor(error.severity)}>
                          {error.severity}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => this.createJiraTicket(error)}
                        >
                          Create JIRA
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {this.renderPagination('ecs', currentPage, totalPages)}
            </>
          ) : (
            <div className="no-errors">
              <i className="fas fa-check-circle text-success"></i>
              <p>No ECS errors found for the selected date.</p>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }


  private getSeverityColor = (severity: string): string => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'danger'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      case 'low':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  private getTodayDate = (): string => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  private refreshData = () => {
    this.loadCloudWatchDataStream()
  }

  private streamResponseParser = (success: boolean, chunk: string) => {
    
    if (!chunk) {
      // Process any remaining buffer
      if (this.streamBuffer.trim()) {
        this.processBufferLines(this.streamBuffer)
      }
      this.streamBuffer = ''
      this.setState(prevState => ({
        streaming: {
          ...prevState.streaming,
          isStreaming: false,
          canCancel: false
        },
        isLoading: false
      }))
      return
    }
    
    if (!success) {
      this.setState(prevState => ({
        streaming: {
          ...prevState.streaming,
          isStreaming: false,
          canCancel: false
        },
        isLoading: false,
        error: chunk || 'Streaming failed'
      }))
      return
    }

    // Add chunk to buffer
    this.streamBuffer += chunk
    this.processBufferLines(this.streamBuffer)
  }

  private processBufferLines = (buffer: string) => {
    try {
      const lines = buffer.split('\n')
      // Keep the last line in buffer as it might be incomplete
      const completeLines = lines.slice(0, -1)
      this.streamBuffer = lines[lines.length - 1] || ''
      
      
      completeLines.forEach(line => {
        if (line.trim()) {
          try {
            const response: Types.StreamChunk = JSON.parse(line.trim())
            this.handleStreamChunk(response)
          } catch (lineError) {
            // ignore
          }
        }
      })
    } catch (e) {
      //ignore
    }
  }

  private handleStreamChunk = (response: Types.StreamChunk) => {
    
    switch (response.type) {
      case 'progress':
        this.setState(prevState => ({
          streaming: {
            ...prevState.streaming,
            progress: {
              current: response.current || 0,
              total: response.total || 0,
              service: response.service || '',
              message: response.message || ''
            }
          }
        }))
        break

      case 'data':
        if (response.service === 'lambda' && response.errors) {
          this.setState(prevState => ({
            streaming: {
              ...prevState.streaming,
              streamedData: {
                ...prevState.streaming.streamedData,
                // Replace with combined errors instead of appending
                lambdaErrors: response.errors as Types.LambdaError[]
              }
            },
            // Update main data in real-time for immediate UI display
            data: {
              ...prevState.data,
              lambdaErrors: response.errors as Types.LambdaError[],
              // Update summary with current counts
              summary: this.calculateRealtimeSummary(
                response.errors as Types.LambdaError[],
                prevState.data.ecsErrors,
                []
              )
            }
          }))
        } else if (response.service === 'ecs' && response.errors) {
          this.setState(prevState => ({
            streaming: {
              ...prevState.streaming,
              streamedData: {
                ...prevState.streaming.streamedData,
                // Replace with combined errors instead of appending
                ecsErrors: response.errors as Types.ECSError[]
              }
            },
            // Update main data in real-time for immediate UI display
            data: {
              ...prevState.data,
              ecsErrors: response.errors as Types.ECSError[],
              // Update summary with current counts
              summary: this.calculateRealtimeSummary(
                prevState.data.lambdaErrors,
                response.errors as Types.ECSError[],
                []
              )
            }
          }))
        }
        break

      case 'service_complete':
        // Service completion - could show a notification
        break

      case 'summary':
        if (response.summary) {
          this.setState(prevState => ({
            streaming: {
              ...prevState.streaming,
              streamedData: {
                ...prevState.streaming.streamedData,
                summary: response.summary
              }
            },
            data: {
              lambdaErrors: prevState.streaming.streamedData.lambdaErrors,
              ecsErrors: prevState.streaming.streamedData.ecsErrors,
              summary: response.summary,
              lastUpdated: response.lastUpdated || new Date().toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })
            }
          }))
        }
        break

      case 'complete':
        this.setState(prevState => ({
          streaming: {
            ...prevState.streaming,
            isStreaming: false,
            canCancel: false,
            progress: {
              current: 0,
              total: 0,
              service: '',
              message: 'Completed successfully'
            }
          },
          isLoading: false,
          // Data is already updated in real-time
          data: {
            ...prevState.data
          },
          // Reset pagination when loading new data
          pagination: {
            lambda: { currentPage: 1, itemsPerPage: 10 },
            ecs: { currentPage: 1, itemsPerPage: 10 },
          }
        }))
        break

      case 'error':
        this.setState(prevState => ({
          streaming: {
            ...prevState.streaming,
            isStreaming: false,
            canCancel: false
          },
          isLoading: false,
          error: response.error || 'Unknown streaming error'
        }))
        break
    }
  }

  private loadCloudWatchDataStream = () => {
    // Reset stream buffer
    this.streamBuffer = ''
    
    this.setState({ 
      isLoading: false, // Allow content to show during streaming
      error: '',
      streaming: {
        isStreaming: true,
        progress: {
          current: 0,
          total: 0,
          service: '',
          message: 'Initializing error search...'
        },
        canCancel: true,
        streamedData: {
          lambdaErrors: [],
          ecsErrors: []
        }
      },
      // Reset pagination when loading new data
      pagination: {
        lambda: { currentPage: 1, itemsPerPage: 10 },
        ecs: { currentPage: 1, itemsPerPage: 10 },
      },
      // Reset data to show empty state initially, then populate as streaming progresses
      data: {
        lambdaErrors: [],
        ecsErrors: [],
        summary: {
          totalErrors: 0,
          criticalErrors: 0,
          highErrors: 0,
          mediumErrors: 0,
          lowErrors: 0,
          errorsByService: {
            lambda: 0,
            ecs: 0,
            sqs: 0
          }
        },
        lastUpdated: ''
      }
    })
    
    // Use streaming endpoint
    const streamUrl = `${AjaxWrapper.getServerUrl()}/api/cloudwatch/errors/stream`
    
    AjaxWrapper.stream(
      streamUrl,
      HttpMethod.POST,
      { 
        date: this.state.selectedDate,
        stream: true 
      },
      this.streamResponseParser
    )
  }



  private cancelStreaming = () => {
    // Note: In a real implementation, you might want to abort the fetch request
    // For now, we'll just reset the streaming state
    this.setState(prevState => ({
      streaming: {
        ...prevState.streaming,
        isStreaming: false,
        canCancel: false,
        progress: {
          current: 0,
          total: 0,
          service: '',
          message: 'Cancelled by user'
        }
      },
      isLoading: false
    }))
  }

  private renderProgressIndicator = () => {
    const { progress } = this.state.streaming
    const { data } = this.state
    const progressPercentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0
    
    // Calculate real-time error counts for display
    const totalErrorsFound = data.lambdaErrors.reduce((sum, error) => sum + error.count, 0) + 
                           data.ecsErrors.reduce((sum, error) => sum + error.count, 0)
    const totalErrorTypes = data.lambdaErrors.length + data.ecsErrors.length
    
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              <i className="fas fa-sync-alt fa-spin mr-2"></i>
              Loading CloudWatch Data - Live Results
            </h6>
            <div className="text-right">
              <small className="text-muted d-block">
                {progress.current}/{progress.total} {progress.service}
              </small>
              {totalErrorsFound > 0 && (
                <small className="text-success d-block">
                  <i className="fas fa-check-circle mr-1"></i>
                  {totalErrorsFound} errors found ({totalErrorTypes} types)
                </small>
              )}
            </div>
          </div>
          
          <div className="progress mb-2" style={{ height: '10px' }}>
            <div 
              className="progress-bar bg-primary" 
              role="progressbar" 
              style={{ width: `${progressPercentage}%` }}
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            ></div>
          </div>
          
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              <i className="fas fa-search mr-1"></i>
              {progress.message}
            </small>
            <small className="text-primary font-weight-bold">
              {progressPercentage}% complete
            </small>
          </div>
          
          {totalErrorsFound > 0 && (
            <div className="mt-2 pt-2 border-top">
              <div className="row text-center">
                <div className="col-4">
                  <small className="text-muted d-block">Lambda Errors</small>
                  <span className="badge badge-primary">
                    {data.lambdaErrors.reduce((sum, error) => sum + error.count, 0)}
                  </span>
                </div>
                <div className="col-4">
                  <small className="text-muted d-block">ECS Errors</small>
                  <span className="badge badge-info">
                    {data.ecsErrors.reduce((sum, error) => sum + error.count, 0)}
                  </span>
                </div>
                <div className="col-4">
                  <small className="text-muted d-block">Critical</small>
                  <span className="badge badge-danger">
                    {data.summary.criticalErrors}
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  private renderPagination = (errorType: 'lambda' | 'ecs', currentPage: number, totalPages: number) => {
    if (totalPages <= 1) return null

    const handlePageChange = (page: number) => {
      this.setState({
        pagination: {
          ...this.state.pagination,
          [errorType]: {
            ...this.state.pagination[errorType],
            currentPage: page
          }
        }
      })
    }

    const pages = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink 
            href="#"
            onClick={(e) => { e.preventDefault(); handlePageChange(i); }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="pagination-info">
          <small className="text-muted">
            Showing {((currentPage - 1) * this.state.pagination[errorType].itemsPerPage) + 1} to{' '}
            {Math.min(currentPage * this.state.pagination[errorType].itemsPerPage, 
              errorType === 'lambda' ? this.state.data.lambdaErrors.length :
              this.state.data.ecsErrors.length)} of{' '}
            {errorType === 'lambda' ? this.state.data.lambdaErrors.length :
             this.state.data.ecsErrors.length} entries
          </small>
        </div>
        <nav aria-label="Error list pagination">
          <Pagination className="pagination mb-0">
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink 
                href="#"
                onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                tabIndex={currentPage === 1 ? -1 : 0}
              >
                Previous
              </PaginationLink>
            </PaginationItem>
            {startPage > 1 && (
              <>
                <PaginationItem>
                  <PaginationLink 
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageChange(1); }}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
                {startPage > 2 && (
                  <PaginationItem disabled>
                    <PaginationLink href="#" tabIndex={-1}>...</PaginationLink>
                  </PaginationItem>
                )}
              </>
            )}
            {pages}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <PaginationItem disabled>
                    <PaginationLink href="#" tabIndex={-1}>...</PaginationLink>
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink 
                    href="#"
                    onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              </>
            )}
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink 
                href="#"
                onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                tabIndex={currentPage === totalPages ? -1 : 0}
              >
                Next
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </nav>
      </div>
    )
  }

  private openLogStreamModal = (error: Types.LambdaError) => {
    
    this.setState({
      logStreamModal: {
        isOpen: true,
        selectedError: error,
        logEvents: [],
        totalEvents: 0,
        aiAnalysis: '',
        analyzedAt: '',
        isLoadingLogs: true,
        error: '',
        logEventsPagination: {
          currentPage: 1,
          itemsPerPage: 10,
        },
      }
    })
    
    this.fetchLogStreamEvents(error)
  }

  private closeLogStreamModal = () => {
    this.setState({
      logStreamModal: {
        isOpen: false,
        selectedError: null,
        logEvents: [],
        totalEvents: 0,
        aiAnalysis: '',
        analyzedAt: '',
        isLoadingLogs: false,
        error: '',
        logEventsPagination: {
          currentPage: 1,
          itemsPerPage: 10,
        },
      }
    })
  }

  private createJiraTicket = (error: Types.LambdaError | Types.ECSError) => {
    // Open modal for confirmation instead of browser confirm
    this.setState({
      jiraModal: {
        isOpen: true,
        selectedError: error,
        isCreatingTicket: false,
      }
    })
  }

  private confirmCreateJiraTicket = () => {
    const error = this.state.jiraModal.selectedError
    if (!error) return

    const isLambdaError = 'functionName' in error
    const serviceName = isLambdaError ? error.functionName : error.serviceName
    const serviceType = isLambdaError ? 'Lambda' : 'ECS'
    
    const ticketTitle = `${serviceType} ${error.severity} Error: ${serviceName} - ${error.errorType}`
    const ticketDescription = `
**Service Details:**
- Type: ${serviceType}
- Name: ${serviceName}
- Error Type: ${error.errorType}
- Severity: ${error.severity}
- Error Count: ${error.count}
- First Occurrence: ${error.firstOccurrence}
- Last Occurrence: ${error.lastOccurrence}

**Error Message:**
${error.message}

**Additional Context:**
${isLambdaError && error.logGroupName ? `Log Group: ${error.logGroupName}` : ''}
${isLambdaError && error.logStreamId ? `Log Stream: ${error.logStreamId}` : ''}

**Action Required:**
This error was detected in the CloudWatch monitoring dashboard and requires investigation.
`.trim()

    this.setState({
      jiraModal: {
        ...this.state.jiraModal,
        isCreatingTicket: true
      }
    })

    AjaxWrapper
      .ajax(`${AjaxWrapper.getServerUrl()}/api/cloudwatch/create-jira-ticket`, HttpMethod.POST, {
        ticketTitle,
        ticketDescription,
        error
      })
      .then((response: any) => {
        this.setState({
          jiraModal: {
            isOpen: false,
            selectedError: null,
            isCreatingTicket: false
          }
        })
        
        if (response.success) {
          this.context.store.dispatch(Flash.showFlashMessage('JIRA ticket created successfully!'))
        } else {
          this.context.store.dispatch(Flash.showFlashError(`Failed to create JIRA ticket: ${response.error || 'Unknown error'}`))
        }
      })
      .catch((err) => {
        this.setState({
          jiraModal: {
            isOpen: false,
            selectedError: null,
            isCreatingTicket: false
          }
        })
        this.context.store.dispatch(Flash.showFlashError('Failed to create JIRA ticket. Please try again.'))
      })
  }

  private closeJiraModal = () => {
    this.setState({
      jiraModal: {
        isOpen: false,
        selectedError: null,
        isCreatingTicket: false
      }
    })
  }

  private calculateRealtimeSummary = (lambdaErrors: Types.LambdaError[], ecsErrors: Types.ECSError[], sqsErrors: any[]): Types.ErrorSummary => {
    const allErrors = [...lambdaErrors, ...ecsErrors, ...sqsErrors]
    
    return {
      totalErrors: allErrors.reduce((sum, error) => sum + error.count, 0),
      criticalErrors: allErrors.filter(e => e.severity === 'Critical').reduce((sum, error) => sum + error.count, 0),
      highErrors: allErrors.filter(e => e.severity === 'High').reduce((sum, error) => sum + error.count, 0),
      mediumErrors: allErrors.filter(e => e.severity === 'Medium').reduce((sum, error) => sum + error.count, 0),
      lowErrors: allErrors.filter(e => e.severity === 'Low').reduce((sum, error) => sum + error.count, 0),
      errorsByService: {
        lambda: lambdaErrors.reduce((sum, error) => sum + error.count, 0),
        ecs: ecsErrors.reduce((sum, error) => sum + error.count, 0),
        sqs: sqsErrors.reduce((sum, error) => sum + error.count, 0)
      }
    }
  }

  private getJiraModalJSX(): JSX.Element {
    const { jiraModal } = this.state
    const error = jiraModal.selectedError
    
    if (!error) return null

    const isLambdaError = 'functionName' in error
    const serviceName = isLambdaError ? error.functionName : error.serviceName
    const serviceType = isLambdaError ? 'Lambda' : 'ECS'
    const ticketTitle = `${serviceType} ${error.severity} Error: ${serviceName} - ${error.errorType}`

    return (
      <Modal isOpen={jiraModal.isOpen}
             className="jira-confirmation-modal"
             size="lg"
             fade={false}
             centered={true}>
        <ModalHeader>Create JIRA Ticket</ModalHeader>
        <ModalBody>
          <div>
            <h5>Confirm JIRA Ticket Creation</h5>
            <p>Are you sure you want to create a JIRA ticket for this {serviceType} error?</p>
            
            <div className="mt-3">
              <strong>Ticket Title:</strong>
              <div className="text-muted small mt-1">{ticketTitle}</div>
            </div>
            
            <div className="mt-3">
              <strong>Service Details:</strong>
              <ul className="text-muted small mt-1">
                <li>Type: {serviceType}</li>
                <li>Name: {serviceName}</li>
                <li>Error Type: {error.errorType}</li>
                <li>Severity: {error.severity}</li>
                <li>Error Count: {error.count}</li>
              </ul>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="primary" 
            onClick={this.confirmCreateJiraTicket}
            disabled={jiraModal.isCreatingTicket}>
            {jiraModal.isCreatingTicket ? 'Creating...' : 'Yes, Create Ticket'}
          </Button>
          <Button 
            color="secondary" 
            onClick={this.closeJiraModal}
            disabled={jiraModal.isCreatingTicket}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private fetchLogStreamEvents = (error: Types.LambdaError) => {
    if (!error.logGroupName || !error.logStreamId) {
      this.setState({
        logStreamModal: {
          ...this.state.logStreamModal,
          isLoadingLogs: false,
          error: 'Missing log stream information. Cannot fetch log events.',
        }
      })
      return
    }
    
    const url = `${AjaxWrapper.getServerUrl()}/api/cloudwatch/logs/${encodeURIComponent(error.logGroupName)}/${encodeURIComponent(error.logStreamId)}`
    
    AjaxWrapper
      .ajax(url, HttpMethod.GET, {})
      .then((response: Types.LogStreamResponse) => {
        if (response.success) {
          this.setState({
            logStreamModal: {
              ...this.state.logStreamModal,
              logEvents: response.data.events,
              totalEvents: response.data.totalEvents,
              aiAnalysis: response.data.aiAnalysis,
              analyzedAt: response.data.analyzedAt,
              isLoadingLogs: false,
              error: '',
              logEventsPagination: {
                currentPage: 1,
                itemsPerPage: 10,
              },
            }
          })
        } else {
          this.setState({
            logStreamModal: {
              ...this.state.logStreamModal,
              isLoadingLogs: false,
              error: 'Failed to fetch log stream events. Please try again.',
            }
          })
        }
      })
      .catch((err) => {
        this.setState({
          logStreamModal: {
            ...this.state.logStreamModal,
            isLoadingLogs: false,
            error: 'Failed to fetch log stream events. Please try again.',
          }
        })
      })
  }

  private renderLogStreamModal = () => {
    const { logStreamModal } = this.state
    const { selectedError } = logStreamModal
    
    if (!selectedError) return null

    return (
      <Modal 
        isOpen={logStreamModal.isOpen} 
        toggle={this.closeLogStreamModal}
        size="xl"
        className="log-stream-modal"
      >
        <ModalHeader toggle={this.closeLogStreamModal}>
          Log Stream Details - {selectedError.functionName}
        </ModalHeader>
        <ModalBody>
          <div className="log-stream-info mb-3">
            <Row>
              <Col md="6">
                <strong>Log Group:</strong> <code>{selectedError.logGroupName}</code>
              </Col>
              <Col md="6">
                <strong>Log Stream:</strong> <code>{selectedError.logStreamId}</code>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md="6">
                <strong>Error Type:</strong> <Badge color="warning">{selectedError.errorType}</Badge>
              </Col>
              <Col md="6">
                <strong>Severity:</strong> <Badge color={this.getSeverityColor(selectedError.severity)}>{selectedError.severity}</Badge>
              </Col>
            </Row>
          </div>
          
          {logStreamModal.error && (
            <Alert color="danger">
              {logStreamModal.error}
            </Alert>
          )}
          
          {logStreamModal.isLoadingLogs ? (
            <div className="text-center">
              <Loading size="32" />
              <p>Loading log events...</p>
            </div>
          ) : (
            <div>
              {logStreamModal.aiAnalysis && (
                <div className="ai-analysis-section mb-4">
                  <Card className="ai-analysis-card">
                    <CardHeader>
                      <h6 className="mb-0">
                        <i className="fas fa-robot mr-2"></i>
                        AI Analysis
                        <small className="text-muted ml-2">
                          Analyzed at {logStreamModal.analyzedAt}
                        </small>
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <div className="ai-analysis-content">
                        <pre className="ai-analysis-text">{logStreamModal.aiAnalysis}</pre>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              )}
              
              <div className="log-events">
                <h6>Log Events ({logStreamModal.totalEvents} total)</h6>
                {logStreamModal.logEvents.length > 0 ? (
                  <>
                    <div className="log-events-list">
                      {this.getPaginatedLogEvents().map((event, index) => (
                        <Card key={index} className="log-event-card mb-2">
                          <CardBody>
                            <div className="log-event-header">
                              <small className="text-muted">
                                <strong>Timestamp:</strong> {event.timestamp} | <strong>Ingestion:</strong> {event.ingestionTime}
                              </small>
                            </div>
                            <pre className="log-event-message mt-2">
                              {event.message}
                            </pre>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                    {this.renderLogEventsPagination()}
                  </>
                ) : (
                  <Alert color="info">
                    No log events found.
                  </Alert>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={this.closeLogStreamModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  private getPaginatedLogEvents = () => {
    const { logStreamModal } = this.state
    const { currentPage, itemsPerPage } = logStreamModal.logEventsPagination
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return logStreamModal.logEvents.slice(startIndex, endIndex)
  }

  private handleLogEventPageChange = (page: number) => {
    this.setState({
      logStreamModal: {
        ...this.state.logStreamModal,
        logEventsPagination: {
          ...this.state.logStreamModal.logEventsPagination,
          currentPage: page,
        },
      }
    })
  }

  private renderLogEventsPagination = () => {
    const { logStreamModal } = this.state
    const { currentPage, itemsPerPage } = logStreamModal.logEventsPagination
    const totalPages = Math.ceil(logStreamModal.logEvents.length / itemsPerPage)
    
    if (totalPages <= 1) return null

    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = Math.min(startIndex + itemsPerPage, logStreamModal.logEvents.length)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="pagination-info">
          <small className="text-muted">
            Showing {startIndex + 1} to {endIndex} of {logStreamModal.logEvents.length} events
          </small>
        </div>
        <nav aria-label="Log events pagination">
          <Pagination className="pagination mb-0">
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink 
                href="#"
                onClick={(e) => { e.preventDefault(); this.handleLogEventPageChange(currentPage - 1); }}
                tabIndex={currentPage === 1 ? -1 : 0}
              >
                Previous
              </PaginationLink>
            </PaginationItem>
            <PaginationItem active>
              <PaginationLink href="#" onClick={(e) => e.preventDefault()}>
                {currentPage}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink 
                href="#"
                onClick={(e) => { e.preventDefault(); this.handleLogEventPageChange(currentPage + 1); }}
                tabIndex={currentPage === totalPages ? -1 : 0}
              >
                Next
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </nav>
      </div>
    )
  }
}

export const CloudWatchMonitoring = GenericRedux.createConnect(CloudWatchMonitoringComponent, Constants.REDUX_STORE_HOME)
injectReducer(
  Constants.REDUX_STORE_HOME,
  Redux.combineReducers({
    data: GenericRedux.createGenericReducer(Constants.REDUX_STORE_HOME, {}),
  }),
)